import SearchIcon from "@mui/icons-material/Search";
import {
    Box,
    Typography,
    TextField,
    Button,
    Skeleton,
    TablePagination,
    MenuItem,
    Select,
    InputAdornment,
    FormControl,
    InputLabel,
    Paper,
} from "@mui/material";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { SearchResultCard } from "./components/SearchResultCard";
import { customInstanceDocumentos } from "../../../api/axios-client";
import type { SearchRequest } from "../../../api/documentos/model/searchRequest";
import type { SearchResponse } from "../../../api/documentos/model/searchResponse";
import { useAuth } from "../../../auth";
import { ActiveFilters } from "../../../components/Search/ActiveFilters";
import { FacetSidebar } from "../../../components/Search/FacetSidebar";

const FACET_LABEL_MAP: Record<string, string> = {
    documentType: "documentsV2.facets.documentType",
    situation: "documentsV2.facets.situation",
    source: "documentsV2.facets.source",
    fileExtension: "documentsV2.facets.fileExtension",
    "authorName.keyword": "documentsV2.facets.authorName",
    createdAt: "documentsV2.facets.createdAt",
};

const VALUE_TRANSLATION_PREFIXES: Record<string, string> = {
    createdAt: "documentsV2.dateRanges",
    source: "documentsV2.sources",
};

const searchSchema = z.object({
    query: z.string().optional(),
    page: z.number().catch(0),
    size: z.number().catch(20),
    sortBy: z.string().catch("_score"),
    sortDirection: z.string().catch("desc"),
    filters: z.string().optional(),
});

type SearchParams = z.infer<typeof searchSchema>;

const FACET_FIELDS = [
    "documentType",
    "situation",
    "source",
    "fileExtension",
    "authorName.keyword",
    "createdAt"
];

export const Route = createFileRoute("/__auth/documents-v2/")({
    component: DocumentsV2Component,
    staticData: {
        title: "routes.documentsV2",
    },
    validateSearch: (search) => searchSchema.parse(search),
});

function parseFilters(filtersStr?: string): Record<string, string[]> {
    if (!filtersStr) return {};
    try {
        return JSON.parse(filtersStr);
    } catch {
        return {};
    }
}

function serializeFilters(filters: Record<string, string[]>): string | undefined {
    const cleaned: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(filters)) {
        if (v.length > 0) cleaned[k] = v;
    }
    if (Object.keys(cleaned).length === 0) return undefined;
    return JSON.stringify(cleaned);
}

function buildSearchRequest(
    params: SearchParams,
    activeFilters: Record<string, string[]>
): SearchRequest {
    let finalQuery = params.query || undefined;
    if (finalQuery) {
        const trimmed = finalQuery.trim();
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
            finalQuery = `"${trimmed}"`;
        } else {
            finalQuery = trimmed;
        }
    }

    const effectiveSortBy =
        (!finalQuery && params.sortBy === "_score") ? "createdAt" : params.sortBy;

    const req: SearchRequest = {
        query: finalQuery,
        page: params.page,
        size: params.size,
        sortBy: effectiveSortBy,
        sortDirection: params.sortDirection,
        facetFields: FACET_FIELDS,
    };

    if (activeFilters.documentType?.length)
        req.documentType = activeFilters.documentType;
    if (activeFilters.situation?.length)
        req.situation = activeFilters.situation;
    if (activeFilters.source?.length)
        req.source = activeFilters.source;
    if (activeFilters.fileExtension?.length)
        req.fileExtension = activeFilters.fileExtension;
    if (activeFilters["authorName.keyword"]?.length)
        req.authorName = activeFilters["authorName.keyword"];
    if (activeFilters.createdAt?.length)
        req.createdAtRange = activeFilters.createdAt;

    return req;
}

function DocumentsV2Component() {
    const { t } = useTranslation();
    const { currentTenant } = useAuth();
    const search = Route.useSearch();
    const navigate = useNavigate({ from: Route.fullPath });

    const [localQuery, setLocalQuery] = useState(search.query || "");

    const activeFilters = useMemo(() => parseFilters(search.filters), [search.filters]);

    const searchRequest = useMemo(
        () => buildSearchRequest(search, activeFilters),
        [search, activeFilters]
    );

    const { data, isLoading } = useQuery({
        queryKey: ["documents-v2-search", currentTenant, searchRequest],
        queryFn: async (): Promise<SearchResponse> => {
            return customInstanceDocumentos<SearchResponse>(
                `/v1/${currentTenant || "default-domain"}/search`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(searchRequest),
                }
            );
        },
        enabled: !!currentTenant,
        placeholderData: keepPreviousData,
    });

    const results = (data?.results || []).filter((r) => !r.score || r.score > 0);
    const totalHits = data?.totalHits || 0;
    const facets = data?.facets;

    const handleSearch = useCallback(() => {
        navigate({
            search: (prev) => ({
                ...prev,
                query: localQuery.trim() || undefined,
                page: 0,
            }),
        });
    }, [navigate, localQuery]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") handleSearch();
    };

    const handleFilterChange = useCallback(
        (facetKey: string, value: string, checked: boolean) => {
            const current = { ...activeFilters };
            const values = current[facetKey] || [];

            if (checked) {
                current[facetKey] = [...values, value];
            } else {
                current[facetKey] = values.filter((v) => v !== value);
            }

            navigate({
                search: (prev) => ({
                    ...prev,
                    filters: serializeFilters(current),
                    page: 0,
                }),
            });
        },
        [navigate, activeFilters]
    );

    const handleRemoveFilter = useCallback(
        (facetKey: string, value: string) => {
            handleFilterChange(facetKey, value, false);
        },
        [handleFilterChange]
    );

    const handleClearAllFilters = useCallback(() => {
        navigate({
            search: (prev) => ({
                ...prev,
                filters: undefined,
                page: 0,
            }),
        });
    }, [navigate]);

    const handlePageChange = (_: unknown, newPage: number) => {
        navigate({
            search: (prev) => ({ ...prev, page: newPage }),
            replace: true,
        });
    };

    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
        navigate({
            search: (prev) => ({
                ...prev,
                size: parseInt(e.target.value, 10),
                page: 0,
            }),
            replace: true,
        });
    };

    const handleSortChange = (sortBy: string) => {
        navigate({
            search: (prev) => ({ ...prev, sortBy, sortDirection: "desc" }),
        });
    };

    const hasActiveFilters = Object.values(activeFilters).some((v) => v.length > 0);

    return (
        <Box sx={{ p: 2, pb: 10 }}>
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                }}
            >
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t("documentsV2.searchPlaceholder")}
                    value={localQuery}
                    onChange={(e) => setLocalQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    variant="outlined"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            bgcolor: "background.default",
                        },
                    }}
                />
                <Button
                    variant="contained"
                    onClick={handleSearch}
                    disableElevation
                    size="large"
                    sx={{
                        whiteSpace: "nowrap",
                        px: 4,
                        textTransform: "uppercase",
                        fontWeight: 600,
                        letterSpacing: 0.5,
                    }}
                >
                    {t("common.search")}
                </Button>
            </Paper>

            {hasActiveFilters && (
                <Box sx={{ mb: 2 }}>
                    <ActiveFilters
                        filters={activeFilters}
                        onRemove={handleRemoveFilter}
                        onClearAll={handleClearAllFilters}
                        labelMap={FACET_LABEL_MAP}
                        clearLabelKey="documentsV2.clearFilters"
                    />
                </Box>
            )}

            {(
                <Box
                    sx={{
                        display: "flex",
                        gap: 2.5,
                        alignItems: "flex-start",
                    }}
                >
                    {facets && Object.keys(facets).length > 0 && (
                        <Box
                            sx={{
                                width: 260,
                                flexShrink: 0,
                                display: { xs: "none", md: "block" },
                                position: "sticky",
                                top: 80,
                            }}
                        >
                            <FacetSidebar
                                facets={facets}
                                activeFilters={activeFilters}
                                onFilterChange={handleFilterChange}
                                labelMap={FACET_LABEL_MAP}
                                valueTranslationPrefixes={VALUE_TRANSLATION_PREFIXES}
                            />
                        </Box>
                    )}

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {!isLoading && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 2,
                                    pb: 1.5,
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                    <Box component="span" sx={{ fontWeight: 700, color: "text.primary", fontSize: "1rem" }}>
                                        {totalHits.toLocaleString()}
                                    </Box>
                                    {" "}{t("documentsV2.results")}
                                </Typography>

                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <InputLabel>{t("documentsV2.sortBy")}</InputLabel>
                                    <Select
                                        value={search.sortBy || "_score"}
                                        label={t("documentsV2.sortBy")}
                                        onChange={(e) => handleSortChange(e.target.value)}
                                    >
                                        <MenuItem value="_score">
                                            {t("documentsV2.relevance")}
                                        </MenuItem>
                                        <MenuItem value="createdAt">
                                            {t("common.creationDate")}
                                        </MenuItem>
                                        <MenuItem value="updatedAt">
                                            {t("documentsV2.lastUpdate")}
                                        </MenuItem>
                                        <MenuItem value="title">
                                            {t("common.title")}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        )}

                        {isLoading ? (
                            <Box>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <Skeleton
                                        key={i}
                                        variant="rectangular"
                                        height={100}
                                        sx={{ mb: 1.5, borderRadius: 2 }}
                                    />
                                ))}
                            </Box>
                        ) : results.length === 0 ? (
                            <Box sx={{ p: 6, textAlign: "center" }}>
                                <Typography variant="h6" color="text.secondary">
                                    {t("documentsV2.noResults")}
                                </Typography>
                            </Box>
                        ) : (
                            results.map((result, index) => (
                                <SearchResultCard
                                    key={result.id || index}
                                    result={result}
                                />
                            ))
                        )}

                        {totalHits > 0 && (
                            <TablePagination
                                component="div"
                                count={totalHits}
                                page={search.page || 0}
                                onPageChange={handlePageChange}
                                rowsPerPage={search.size || 20}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage={t("common.itemsPerPage")}
                                labelDisplayedRows={({ from, to, count }) =>
                                    `${from}–${to} de ${count}`
                                }
                                rowsPerPageOptions={[10, 20, 50, 100]}
                                sx={{
                                    mt: 2,
                                    borderTop: "1px solid",
                                    borderColor: "divider",
                                }}
                            />
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
}
